import { useState } from "preact/hooks";
import type { CrmData } from "../../types";
import { PRODUCT_CATALOG } from "../../types";
import { Drawer } from "../ui/Modal";
import { Badge, PriorityBadge, ProposalStatusBadge, StageBadge } from "../ui/Badge";
import { Avatar } from "../ui/Avatar";
import { computeWarnings, isTerminalStage, needsDiscountApproval, canAcceptProposal } from "../../lib/businessRules";
import { activitiesFor, proposalsFor } from "../../lib/selectors";
import { formatDate, money } from "../../lib/format";
import { store } from "../../store/store";
import { toast } from "../shell/Toast";
import {
  AddActivityModal,
  ChangeOwnerModal,
  EditOpportunityModal,
  MarkLostModal,
  MarkWonModal,
  ProposalFormModal,
  ScheduleFollowUpModal,
} from "./OpportunityModals";
import { InvoiceFormModal } from "../billing/InvoiceFormModal";
import { invoicesForOpportunity } from "../../lib/billing";

type ActiveModal = "edit" | "activity" | "proposal" | "won" | "lost" | "owner" | "follow-up" | "generate-invoice" | null;

const ACTIVITY_ICON: Record<string, string> = {
  call: "📞",
  email: "✉️",
  meeting: "🗓️",
  note: "📝",
  task: "✅",
  "proposal-sent": "📄",
  "stage-changed": "➡️",
  "lost-reason": "⚠️",
  "contract-confirmed": "✍️",
};

export function OpportunityDetail({
  opportunityId,
  data,
  now,
  onClose,
  onOpenInvoice,
}: {
  opportunityId: string;
  data: CrmData;
  now: Date;
  onClose: () => void;
  onOpenInvoice: (invoiceId: string) => void;
}) {
  const [modal, setModal] = useState<ActiveModal>(null);

  const opportunity = data.opportunities.find((o) => o.id === opportunityId);
  if (!opportunity) {
    return (
      <Drawer title="Opportunity not found" onClose={onClose}>
        <p>This opportunity no longer exists.</p>
      </Drawer>
    );
  }

  const company = data.companies.find((c) => c.id === opportunity.companyId);
  const contact = data.contacts.find((c) => c.id === opportunity.primaryContactId);
  const contacts = data.contacts.filter((c) => c.companyId === opportunity.companyId);
  const proposals = proposalsFor(opportunity.id, data.proposals);
  const activities = activitiesFor(opportunity.id, data.activities);
  const warnings = computeWarnings(opportunity, data.activities, data.proposals, data.contacts, now);
  const latestProposal = proposals[0];
  const product = PRODUCT_CATALOG.find((p) => p.id === opportunity.productId);
  const terminal = isTerminalStage(opportunity.stage);

  function acceptProposal(proposalId: string) {
    const proposal = proposals.find((p) => p.id === proposalId);
    if (!proposal) return;
    const check = canAcceptProposal(proposal, now);
    if (!check.allowed) {
      toast.error(check.reason ?? "This proposal cannot be accepted.");
      return;
    }
    store.updateProposal(proposalId, { status: "Accepted" });
    toast.success("Proposal accepted.");
  }

  return (
    <div>
      <Drawer
        onClose={onClose}
        title={
          <div>
            <div class="drawer__breadcrumb">
              <Avatar name={company?.name ?? "Unknown"} size="sm" />
              {company?.name ?? "Unknown company"}
            </div>
            <h2 class="drawer__opp-name">{opportunity.name}</h2>
            <div class="drawer__badges">
              <StageBadge stage={opportunity.stage} />
              <PriorityBadge priority={opportunity.priority} />
              {warnings.map((w) => (
                <Badge key={w.code} tone={w.severity === "danger" ? "danger" : w.severity === "info" ? "info" : "warning"}>
                  {w.label}
                </Badge>
              ))}
            </div>
          </div>
        }
      >
        <div class="drawer-grid">
          <div class="drawer-grid__stats">
            <div class="stat">
              <span class="stat__label">Estimated value</span>
              <span class="stat__value">{money(opportunity.value)}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Probability</span>
              <span class="stat__value">{opportunity.probability}%</span>
            </div>
            <div class="stat">
              <span class="stat__label">Sales owner</span>
              <span class="stat__value">{opportunity.salesOwner}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Expected close</span>
              <span class="stat__value">
                {formatDate(opportunity.expectedCloseDate)}
              </span>
            </div>
          </div>

          <section class="drawer-section">
            <h3>Overview</h3>
            <p>{opportunity.description}</p>
            <dl class="detail-list">
              <div>
                <dt>Product / service</dt>
                <dd>{product?.name ?? "Not selected"}</dd>
              </div>
              <div>
                <dt>Lead source</dt>
                <dd>{opportunity.source}</dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{formatDate(opportunity.createdAt)}</dd>
              </div>
              <div>
                <dt>Competitor</dt>
                <dd>{opportunity.competitor ?? "—"}</dd>
              </div>
              <div>
                <dt>Tags</dt>
                <dd>{opportunity.tags.length ? opportunity.tags.join(", ") : "—"}</dd>
              </div>
              {opportunity.stage === "Lost" && (
                <div>
                  <dt>Lost reason</dt>
                  <dd>{opportunity.lostReason}</dd>
                </div>
              )}
            </dl>
            {opportunity.internalNotes && (
              <div class="internal-note">
                <strong>Internal notes:</strong> {opportunity.internalNotes}
              </div>
            )}
          </section>

          <section class="drawer-section">
            <h3>Primary Contact</h3>
            {contact ? (
              <div class="contact-card">
                <Avatar name={contact.name} size="lg" />
                <div class="contact-card__body">
                  <div class="contact-card__name">
                    {contact.name}
                    {contact.decisionMaker && <Badge tone="success">Decision maker</Badge>}
                  </div>
                  <div class="contact-card__role">{contact.role}</div>
                  <dl class="detail-list">
                    <div>
                      <dt>Email</dt>
                      <dd>{contact.email}</dd>
                    </div>
                    <div>
                      <dt>Phone</dt>
                      <dd>{contact.phone}</dd>
                    </div>
                    <div>
                      <dt>Influence</dt>
                      <dd>{contact.influenceLevel}</dd>
                    </div>
                    <div>
                      <dt>Decision maker</dt>
                      <dd>{contact.decisionMaker ? "Yes" : "No"}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            ) : (
              <p class="empty-inline">No primary contact selected yet.</p>
            )}
          </section>

          <section class="drawer-section">
            <div class="row spread">
              <h3>Activities Timeline</h3>
              <button type="button" class="btn btn--secondary btn--small" data-ai-id="add-activity" onClick={() => setModal("activity")}>
                + Add Activity
              </button>
            </div>
            {activities.length === 0 && <p class="empty-inline">No activities logged yet.</p>}
            <ol class="timeline">
              {activities.map((a) => (
                <li key={a.id} class="timeline__item">
                  <span class="timeline__icon" aria-hidden="true">
                    {ACTIVITY_ICON[a.type] ?? "•"}
                  </span>
                  <div class="timeline__content">
                    <div class="timeline__meta">
                      <strong>{a.author}</strong> · {formatDate(a.timestamp)} · {a.type.replace("-", " ")}
                    </div>
                    <div class="timeline__text">{a.content}</div>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section class="drawer-section">
            <div class="row spread">
              <h3>Proposal</h3>
              <button type="button" class="btn btn--secondary btn--small" data-ai-id="create-proposal" onClick={() => setModal("proposal")}>
                + Create Proposal
              </button>
            </div>
            {!latestProposal && <p class="empty-inline">No proposal has been created yet.</p>}
            {proposals.map((p) => (
              <div key={p.id} class="proposal-card">
                <div class="row spread">
                  <strong>{p.proposalNumber}</strong>
                  <ProposalStatusBadge status={p.status} />
                </div>
                <dl class="detail-list">
                  <div>
                    <dt>Amount</dt>
                    <dd>{money(p.amount)}</dd>
                  </div>
                  <div>
                    <dt>Discount</dt>
                    <dd>{p.discountPercent}%</dd>
                  </div>
                  <div>
                    <dt>Generated</dt>
                    <dd>{formatDate(p.generatedDate)}</dd>
                  </div>
                  <div>
                    <dt>Sent</dt>
                    <dd>{formatDate(p.sentDate)}</dd>
                  </div>
                  <div>
                    <dt>Expires</dt>
                    <dd>{formatDate(p.expirationDate)}</dd>
                  </div>
                </dl>
                {needsDiscountApproval(p) && (
                  <div class="form-warning row spread">
                    <span>Discount above 20% requires manager approval.</span>
                    <button
                      type="button"
                      class="btn btn--secondary btn--small"
                      data-ai-id={`approve-discount-${p.id}`}
                      onClick={() => {
                        store.updateProposal(p.id, { approved: true });
                        toast.success("Discount approved.");
                      }}
                    >
                      Approve discount
                    </button>
                  </div>
                )}
                <div class="row" style={{ marginTop: "8px" }}>
                  {p.status === "Draft" && (
                    <button type="button" class="btn btn--secondary btn--small" onClick={() => store.setProposalStatus(p.id, "Sent")}>
                      Mark as Sent
                    </button>
                  )}
                  {p.status === "Sent" && (
                    <button type="button" class="btn btn--secondary btn--small" onClick={() => store.setProposalStatus(p.id, "Viewed")}>
                      Mark as Viewed
                    </button>
                  )}
                  {(p.status === "Sent" || p.status === "Viewed") && (
                    <>
                      <button type="button" class="btn btn--secondary btn--small" onClick={() => acceptProposal(p.id)}>
                        Accept
                      </button>
                      <button type="button" class="btn btn--secondary btn--small" onClick={() => store.setProposalStatus(p.id, "Rejected")}>
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </section>

          <section class="drawer-section actions-bar">
            <h3>Actions</h3>
            <div class="row" style={{ flexWrap: "wrap" }}>
              <button type="button" class="btn btn--secondary" data-ai-id="edit-opportunity" onClick={() => setModal("edit")}>
                Edit opportunity
              </button>
              <button type="button" class="btn btn--secondary" data-ai-id="change-owner" onClick={() => setModal("owner")}>
                Change owner
              </button>
              <button type="button" class="btn btn--secondary" data-ai-id="schedule-follow-up" onClick={() => setModal("follow-up")}>
                Schedule follow-up
              </button>
              {opportunity.stage === "Won" && (
                <button type="button" class="btn btn--primary" data-ai-id="generate-invoice" onClick={() => setModal("generate-invoice")}>
                  {invoicesForOpportunity(opportunity.id, data.invoices).length > 0 ? "Generate another invoice" : "Generate invoice"}
                </button>
              )}
              {!terminal && (
                <>
                  <button type="button" class="btn btn--primary" data-ai-id="mark-opportunity-won" onClick={() => setModal("won")}>
                    Mark as Won
                  </button>
                  <button type="button" class="btn btn--danger" data-ai-id="mark-opportunity-lost" onClick={() => setModal("lost")}>
                    Mark as Lost
                  </button>
                </>
              )}
            </div>
          </section>
        </div>
      </Drawer>

      {modal === "edit" && <EditOpportunityModal opportunity={opportunity} contacts={contacts} onClose={() => setModal(null)} onDone={() => setModal(null)} />}
      {modal === "activity" && <AddActivityModal opportunity={opportunity} onClose={() => setModal(null)} onDone={() => setModal(null)} />}
      {modal === "proposal" && <ProposalFormModal opportunity={opportunity} onClose={() => setModal(null)} onDone={() => setModal(null)} />}
      {modal === "won" && <MarkWonModal opportunity={opportunity} onClose={() => setModal(null)} onDone={() => setModal("generate-invoice")} />}
      {modal === "lost" && <MarkLostModal opportunity={opportunity} onClose={() => setModal(null)} onDone={() => setModal(null)} />}
      {modal === "owner" && <ChangeOwnerModal opportunity={opportunity} onClose={() => setModal(null)} onDone={() => setModal(null)} />}
      {modal === "follow-up" && <ScheduleFollowUpModal opportunity={opportunity} onClose={() => setModal(null)} onDone={() => setModal(null)} />}
      {modal === "generate-invoice" && (
        <InvoiceFormModal
          companies={data.companies}
          opportunities={data.opportunities}
          fromOpportunity={opportunity}
          onClose={() => setModal(null)}
          onCreated={(invoiceId) => {
            setModal(null);
            onClose();
            onOpenInvoice(invoiceId);
          }}
        />
      )}
    </div>
  );
}
